// src/pages/Dashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Dashboard.module.css";
import api from "../api";
import { useIsMobile } from "../hooks/useIsMobile";

export default function Dashboard() {
  const navigate = useNavigate();

  const [taskNotifCounts, setTaskNotifCounts] = useState({});
  const [chatMyUnreadCount,    setChatMyUnreadCount]    = useState(0);
  const [chatOtherUnreadCount, setChatOtherUnreadCount] = useState(0);
  const [mailAlertBadge, setMailAlertBadge] = useState(0);

  // ✅ 공용메일 알림 미확인 건수 — 별도 도메인이라 위 task Promise.all과 묶지 않고
  // 독립 useEffect로 분리(하나가 실패해도 다른 배지에 영향 없게).
  useEffect(() => {
    api.get("/mail-alert/logs/unread-count/")
      .then((res) => setMailAlertBadge(res.data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/tasks/notifications/"),
      api.get("/tasks/assigned/"),
      api.get("/tasks/received/"),
      api.get("/users/me/"),
    ]).then(([r1, r2, r3, r4]) => {

      // ── 기존: 헤더 알림 카운트 ──────────────────────────
      const counts = {};
      r1.data.forEach(n => {
        if (!n.is_read) counts[n.ntype] = (counts[n.ntype] || 0) + 1;
      });
      setTaskNotifCounts(counts);

      // ── 신규: 채팅 페이지와 동일한 방식으로 계산 ──────────
      const me = r4.data.id;

      const myUnreadIds = new Set(
        r1.data.filter(n => !n.is_read).map(n => Number(n.task))
      );

      const seen = new Set();
      const allTasks = [
        ...r2.data,
        ...r3.data,
      ].filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      }).filter(t => t.comment_count > 0 && !t.chat_closed);

      setChatMyUnreadCount(
        allTasks.filter(t => myUnreadIds.has(t.id)).length
      );
      setChatOtherUnreadCount(
        allTasks.filter(t => !myUnreadIds.has(t.id) && t.last_comment_author === me).length
      );

    }).catch(() => {});
  }, []);

  // 기존 헤더 알림 뱃지는 그대로 유지
  const assignListBadge =
    (taskNotifCounts["task_completed"] || 0) +
    (taskNotifCounts["task_comment_to_assigner"] || 0) +
    (taskNotifCounts["task_question"] || 0);

  const receivedListBadge =
    (taskNotifCounts["task_assigned"] || 0) +
    (taskNotifCounts["task_comment_to_assignee"] || 0) +
    (taskNotifCounts["task_answer"] || 0);


  // ✅ 추가: 유저 권한 정보
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isRegularOrAbove = ["ceo", "regular"].includes(user?.employee_type);
  const canSeeWelfare =
    user?.employee_type === "ceo" ||
    (user?.employee_type === "regular" && user?.grade <= 8);

    
    // ✅ 업무 성과 대시보드 열람 권한 (백엔드 연동: 관리자 + 허용된 사람)
    const [canSeeWorkDashboard, setCanSeeWorkDashboard] = useState(false);
  
    useEffect(() => {
      api.get("work-performance/viewers/")
        .then(r => {
          const ids = r.data.viewer_ids || [];
          setCanSeeWorkDashboard(!!r.data.can_manage || ids.includes(user?.id));
        })
        .catch(() => {});
    }, [user?.id]);


  // 상단 탭 (탭스라벨)
  
  const TABS = [
    // 0줄
    // ✅ 금천아 빌려줘(카페24 대여 주문) — 첫 줄 맨 왼쪽(2026-09-29 배치 지시).
    { key: "rental", label: "금천아 빌려줘" },
    // ✅ 에어컨 청소 예약(홈페이지 연동, 2026-10-19) — 대여 주문과 같은 줄, 바로 옆.
    { key: "aircon", label: "에어컨 청소" },
    // ✅ 카페 업무 — 업무일정표 단독 탭이던 자리를 그룹으로 확장(기타 업무와 같은
    // 카드 패턴). navigateTo를 빼야 클릭 시 바로 이동하지 않고 하위 카드가 열린다.
    { key: "cafe", label: "카페 업무", breakAfter: true },
    // 1줄
    { key: "income", label: "수입 데이터" },
    { key: "expense", label: "지출데이터+지출 기안 작성" },
    { key: "approval", label: "전자결재" },
    { key: "accounting", label: "회계 업무" },
    { key: "service_biz", label: "용역 업무", breakAfter: true },
    // 2줄
    { key: "task", label: "업무 관리" },
    { key: "edu", label: "교육 관리" },
    { key: "hreval", label: "인사평가" },
    { key: "evaluation", label: "경영평가", breakAfter: true },
    // 3줄
    { key: "etc", label: "기타 업무" },
  ];

  // 탭별 섹션(카드) + 메뉴 항목
  const SECTIONS = {
    rental: [
      {
        title: "금천아 빌려줘 (대여 주문)",
        items: [
          { label: "주문 처리(오늘 배송·회수)", code: "rental_orders" },
          { label: "매출 현황", code: "rental_sales" },
        ],
      },
    ],
    aircon: [
      {
        title: "에어컨 청소 (홈페이지 예약)",
        items: [
          { label: "예약 목록", code: "aircon_reservations" },
        ],
      },
    ],
    cafe: [
      {
        title: "카페 업무",
        items: [
          { label: "업무일정표", code: "cafe_work_schedule" },
          { label: "카페 지출 기안 대장", code: "cafe_expense_ledger" },
          // ✅ 기타 업무에서 이동 — 미확인 배지는 그대로 유지
          { label: "공용메일 알림", code: "etc_mail_alert", badge: mailAlertBadge },
        ],
      },
    ],
    task: [
      {
        title: "읽지 않은 메시지",
        items: [
          { label: "내가 읽지 않은 메시지",    code: "task_chat_mine",  badge: chatMyUnreadCount },
          { label: "상대방이 읽지 않은 메시지", code: "task_chat_other", badge: chatOtherUnreadCount }, 
        ],
      },
      {
        title: "업무 지시/협조 요청",
        items: [
          { label: "[alt + 2] 업무 요청", code: "task_assign_new" },
          { label: "내가 지시/협조 요청한 목록", code: "task_assign_list", badge: assignListBadge },
        ],
      },
      {
        title: "받은 지시/협조 업무",
        items: [
          { label: "[alt + 3] 받은 업무 목록", code: "task_received_list", badge: receivedListBadge },
          { label: "직원별 해야 할 업무 목록", code: "task_by_employee" }, 
        ],
      },
      {
        title: "업무처리",
        items: [
          { label: "[alt + 1] 빠른 입력", code: "work_performance_quick" },
          { label: "업무처리 현황", code: "work_performance_summary" },
        ],
      },

      ...(canSeeWorkDashboard ? [{
        title: "업무 성과",
        items: [
          { label: "업무 성과 대시보드(일부공개)", code: "work_performance_dashboard" },
        ],
      }] : []),

      {
        title: "일별 TO DO (아침 회의)",
        items: [
          { label: "오늘의 주요 업무 입력", code: "daily_todo_list" },
          { label: "팀 회의 모드(팀장)", code: "daily_todo_meeting" },
          { label: "기간별 완료율 통계", code: "daily_todo_stats" },
        ],
      },

    ],

    edu: [
      {
        title: "교육 관리",
        items: [
          { label: "직무교육", code: "edu_job" },
          { label: "유튜브 강의", code: "edu_youtube" },
          { label: "법정의무교육(현장근로자)", code: "edu_legal" },
          { label: "개인별 수료증 관리(사무직원)", code: "edu_cert" },
        ],
      },
    ],



    approval: [
      {
        title: "수신 결재",
        items: [
          { label: "수신 결재", code: "approval_inbox" },
          { label: "결재 완료", code: "approval_done" },
        ],
      },
      {
        title: "결재 진행 상황",
        items: [
          { label: "결재 진행 중", code: "approval_inprogress" },
          { label: "결재 완료", code: "approval_done2" },
        ],
      },
      {
        title: "수신참조",
        items: [{ label: "수신 참조", code: "approval_cc" }],
      },
      {
        title: "휴가 신청",
        items: [
          { label: "휴가신청서 작성", code: "leave_request_new" },
          { label: "휴가신청 목록", code: "leave_request_list" },
        ],
      },
      {
        title: "기안 작성",
        items: [
          { label: "수입기안 작성", code: "income_draft_new" },
          // ✅ "지출기안 작성"(draft_document_new) 버튼 숨김 — 지출 기안은 이제 각
          // 사업 통합 페이지("지출데이터+기안 작성", expense 탭)에서 작성한다.
          // (구) 버튼 정리와 동일 원칙: 버튼만 제거, 라우트(handleItemClick의
          // draft_document_new 분기)와 페이지는 보존해 URL 직접 접근은 유지.
          { label: "기안 목록 페이지", code: "draft_document_list" },
        ],
      },
      {
        title: "결의 작성",
        items: [
          { label: "(경영팀) 수입결의 작성", code: "income_resolution_new" },
          { label: "(경영팀) 지출결의 작성", code: "expense_resolution_new" },
          { label: "(경영팀) 결의 목록 페이지", code: "resolution_list" },
        ],
      },
    ],

    
    income: [
      {
        title: "경영팀 (기타매출 등)",
        items: [
          // ✅ 수입데이터+수입기안 통합 작성 [파일럿] — (구) 버튼은 그대로 두고 신규만 추가.
          { label: "경영팀 수입데이터+기안 작성", code: "income_business_draft_new" },
          { label: "경영팀 수입목록(신규)", code: "income_business_draft_list" },
          { label: "(구) 경영팀 수입 건", code: "income_business_new", path: "/income/business/new" },
          { label: "(구) 경영팀 수입목록", code: "income_business_list", path: "/income/business/list" },
        ],
      },
      {
        title: "용역사업 (기성계 등)",
        items: [
          // ✅ 수입데이터+수입기안 통합 작성 [4단계] — (구) 버튼은 그대로 두고 신규만 추가.
          { label: "용역사업 수입데이터+기안 작성", code: "income_service_draft_new" },
          { label: "용역사업 수입목록(신규)", code: "income_service_draft_list" },
          { label: "(구) 용역사업 수입 건", code: "income_service_new" },
          { label: "(구) 용역사업 수입목록", code: "income_service_list", path: "/income/service/list" },
        ],
      },
      {
        title: "환경/소독 (기성계 등)",
        items: [
          // ✅ 수입데이터+수입기안 통합 작성 [3단계] — (구) 버튼은 그대로 두고 신규만 추가.
          { label: "환경/소독 수입데이터+기안 작성", code: "income_env_disinfection_draft_new" },
          { label: "환경/소독 수입목록(신규)", code: "income_env_disinfection_draft_list" },
          { label: "(구) 환경/소독 수입 건", code: "income_env_disinfection_new" },
          { label: "(구) 환경/소독 수입목록", code: "income_env_disinfection_list", path: "/income/env/disinfection/list" },
          { label: "환경/소독 청구서", code: "income_env_disinfection_invoice", path: "/income/env/disinfection/invoice" },
          { label: "환경/소독 거래명세서", code: "income_env_disinfection_statement", path: "/income/env/disinfection/statement" },
        ],
      },
      {
        title: "환경/종합 (기성계 등)",
        breakAfter: true,
        items: [
          // ✅ 수입데이터+수입기안 통합 작성 [2단계] — (구) 버튼은 그대로 두고 신규만 추가.
          { label: "환경/종합 수입데이터+기안 작성", code: "income_env_total_draft_new" },
          { label: "환경/종합 수입목록(신규)", code: "income_env_total_draft_list" },
          { label: "(구) 환경/종합 수입 건", code: "income_env_total_new" },
          { label: "(구) 환경/종합 수입목록", code: "income_env_total_list", path: "/income/env/total/list" },
          { label: "환경/종합 거래명세서", code: "income_env_total_statement", path: "/income/env/total/statement" },
        ],
      },
      {
        title: "카페인 가산점 (매출자료 등)",
        items: [
          { label: "가산점 수입 건", code: "income_cafe_gasan_new" },
          { label: "가산점 수입목록", code: "income_cafe_gasan_list", path: "/income/cafe/gasan/list" },
        ],
      },
      {
        title: "카페인 독산점 (매출자료 등)",
        items: [
          { label: "독산점 수입 건", code: "income_cafe_doksan_new" },
          { label: "독산점 수입목록", code: "income_cafe_doksan_list", path: "/income/cafe/doksan/list" },
        ],
      },
      {
        title: "카페인 금나래점 (매출자료 등)",
        items: [
          { label: "금나래점 수입 건", code: "income_cafe_geumnarae_new" },
          { label: "금나래점 수입목록", code: "income_cafe_geumnarae_list", path: "/income/cafe/geumnarae/list" },
        ],
      },
      {
        title: "카페인 그린점 (매출자료 등)",
        items: [
          { label: "그린점 수입 건", code: "income_cafe_green_new" },
          { label: "그린점 수입목록", code: "income_cafe_green_list", path: "/income/cafe/green/list" },
        ],
      },
    ],

    
    // ✅ Dashboard 지출 메뉴 정리 — (구) 단독 페이지 16개(8개 사업 × 2)는 버튼만
    // 숨김. 라우트/페이지 코드는 그대로 둬서 URL 직접 접근은 계속 가능(아래
    // handleItemClick의 expense_mgmt_new/list 등 (구) code 분기도 보존). 이 페이지들이
    // 안정화 이후 완전히 대체됐다고 확정되면 그때 라우트·페이지 파일까지 일괄 삭제 예정.
    expense: [
      {
        title: "경영팀 (본사 지출 등)",
        items: [
          { label: "경영팀 지출데이터+기안 작성", code: "expense_business_draft_new" },
          { label: "경영팀 지출목록", code: "expense_business_draft_list" },
        ],
      },
      {
        title: "용역사업 (구매/인건비 등)",
        items: [
          { label: "용역사업 지출데이터+기안 작성", code: "expense_service_draft_new" },
          { label: "용역사업 지출목록", code: "expense_service_draft_list" },
        ],
      },
      {
        title: "환경/소독 (구매/인건비 등)",
        items: [
          { label: "환경/소독 지출데이터+기안 작성", code: "expense_env_disinfection_draft_new" },
          { label: "환경/소독 지출목록", code: "expense_env_disinfection_draft_list" },
        ],
      },
      {
        title: "환경/종합 (구매/인건비 등)",
        breakAfter: true,
        items: [
          { label: "환경/종합 지출데이터+기안 작성", code: "expense_env_total_draft_new" },
          { label: "환경/종합 지출목록", code: "expense_env_total_draft_list" },
        ],
      },
      {
        title: "카페인 가산점 (구매/인건비 등)",
        items: [
          { label: "가산점 지출데이터+기안 작성", code: "expense_cafe_gasan_draft_new" },
          { label: "가산점 지출목록", code: "expense_cafe_gasan_draft_list" },
        ],
      },
      {
        title: "카페인 독산점 (구매/인건비 등)",
        items: [
          { label: "독산점 지출데이터+기안 작성", code: "expense_cafe_doksan_draft_new" },
          { label: "독산점 지출목록", code: "expense_cafe_doksan_draft_list" },
        ],
      },
      {
        title: "카페인 금나래점 (구매/인건비 등)",
        items: [
          { label: "금나래점 지출데이터+기안 작성", code: "expense_cafe_geumnarae_draft_new" },
          { label: "금나래점 지출목록", code: "expense_cafe_geumnarae_draft_list" },
        ],
      },
      {
        title: "카페인 그린점 (구매/인건비 등)",
        items: [
          { label: "그린점 지출데이터+기안 작성", code: "expense_cafe_green_draft_new" },
          { label: "그린점 지출목록", code: "expense_cafe_green_draft_list" },
        ],
      },
      {
        title: "테스트 (신기능 검증용)",
        items: [
          { label: "테스트 지출 건", code: "expense_test_new" },
          { label: "테스트 지출목록", code: "expense_test_list" },
        ],
      },
    ],


    accounting: [
      {
        title: "일반회계 (카드수수료 포함)",
        items: [
          { label: "(일반) 매출자료 작성", code: "acct_general_income_form" },
          { label: "(일반) 매출자료 목록", code: "acct_general_income_list" },
          { label: "(일반) 지출자료 작성", code: "acct_general_expense_form" },
          { label: "(일반) 지출자료 목록", code: "acct_general_expense_list" },
        ],
      },
      {
        title: "자금월보 (카드수수료 제외)",
        items: [
          { label: "입금내역 작성", code: "acct_cash_deposit_form" },
          { label: "입금내역 목록", code: "acct_cash_deposit_list" },
          { label: "지출내역 작성", code: "acct_cash_expense_form" },
          { label: "지출내역 목록", code: "acct_cash_expense_list" },
          { label: "자금월보 작성", code: "acct_cash_monthly_form" },
          { label: "자금월보 목록", code: "acct_cash_monthly_list" },
        ],
      },
      {
        title: "수입 결산",
        items: [
          { label: "수입결산 작성", code: "acct_income_closing_form" },
          { label: "수입결산 목록", code: "acct_income_closing_list" },
          { label: "수입결의서 작성", code: "acct_income_form" },
          { label: "수입결의서 목록", code: "acct_income_list" },
        ],
      },
      {
        title: "지출 업무",
        items: [
          { label: "지출결산 작성", code: "acct_expense_closing_form" },
          { label: "지출결산 목록", code: "acct_expense_closing_list" },
          { label: "지출결의서 작성", code: "acct_expense_form" },
          { label: "지출결의서 목록", code: "acct_expense_list" },
        ],
      },
      {
        title: "예산 업무",
        items: [
          { label: "연도별 예산서 작성", code: "acct_budget_yearly_form" },
          { label: "연도별 예산서 목록", code: "acct_budget_yearly_list" },
        ],
      },
      {
        title: "결산 업무",
        items: [
          { label: "연도별 예산 결산 / 지출 결산 작성", code: "acct_report_budget_closing_form" },
          { label: "연도별 예산 결산 / 지출 결산 목록", code: "acct_report_budget_closing_list" },
        ],
      },
      {
        title: "계정과목 (일반회계)",
        items: [
          { label: "(일반) 매출 계정과목 작성", code: "acct_account_sales_form" },
          { label: "(일반) 매출 계정과목 목록", code: "acct_account_sales_list" },
          { label: "(일반) 매입 계정과목 작성", code: "acct_account_purchase_form" },
          { label: "(일반) 매입 계정과목 목록", code: "acct_account_purchase_list" },
        ],
      },
      {
        title: "계정과목 (예산결산)",
        items: [
          { label: "(예결) 예산 계정과목 작성", code: "acct_account_budget_form" },
          { label: "(예결) 예산 계정과목 목록", code: "acct_account_budget_list" },
          { label: "(예결) 지출 계정과목 작성", code: "acct_account_expense_form" },
          { label: "(예결) 지출 계정과목 목록", code: "acct_account_expense_list" },
        ],
      },
      {
        title: "통장 및 카드",
        items: [
          { label: "통장 등록", code: "acct_bank_register" },
          { label: "카드 등록", code: "acct_card_register" },
          { label: "통장관리대장", code: "acct_bank_ledger" },
        ],
      },
      {
        title: "기성계 자동화",
        items: [
          { label: "계약 등록/관리", code: "gisung_contract_list", path: "/gisung-gye/contracts" },
          { label: "기성계 생성 (구청 등)", code: "gisung_guchung", path: "/gisung-gye/guchung" },
          { label: "기성계 생성 (공단)", code: "gisung_gongdan", path: "/gisung-gye/gongdan" },
          { label: "기성 생성 이력", code: "gisung_history", path: "/gisung-gye/history" },
        ],
      },
      {
        title: "사회보험",
        items: [
          { label: "사회보험 관리", code: "etc_insurance_allocation" },
          { label: "개인별 연간 합계", code: "etc_insurance_annual" },   // ✅ 추가
          { label: "4대보험 요율 관리", code: "insurance_rate_manage" },

        ],
      },
      {
        title: "급여",
        items: [
          { label: "근로소득 간이세액표", code: "payroll_tax_table" },
          { label: "시급 설정", code: "payroll_wage_rates" },
          { label: "급여명세서", code: "payroll_sheet" },   

        ],
      },

    ],

    service_biz: [
      {
        title: "용역 견적서",
        items: [
          { label: "견적서 작성", code: "quotation_new", path: "/quotation/new" },
          { label: "견적서 목록", code: "quotation_list", path: "/quotation/list" },
        ],
      },
    ],

    hreval: [
      {
        title: "인사평가",
        items: [
          { label: "일용직 인사평가",  code: "hreval_list" },
          { label: "계약직 인사평가",  code: "hreval_contract" },
        ],
      },
    ],
    
    evaluation: [
      {
        title: "경영평가",
        items: [
          { label: "연도별 평가편람", code: "evaluation_manual_list" },
        ],
      },
    ],
    etc: [
      {
        title: "기타 업무",
        items: [
          { label: "임직원 연락망", code: "etc_staff_contacts" },
          { label: "거래처", code: "etc_vendors" },
          { label: "ID/PW", code: "etc_websites" },         

          { label: "지출 사용내역 코드 관리", code: "etc_expense_usage_codes" },
        ],
      },

      // ✅ 추가: 팀장 사용설명서
      {
        title: "사용설명서",
        items: [
          { label: "팀장 사용설명서", code: "etc_manual_guide" },
        ],
      },


      ...(canSeeWelfare ? [{
        title: "복지 업무",
        items: [
          { label: "복지포인트", code: "etc_welfare_point" },
        ],
      }] : []),

      {
        title: "캘린더",
        items: [
          { label: "[alt + `] 캘린더(일반)", code: "etc_calendar_general" },
          { label: "캘린더(관리자)", code: "etc_calendar_admin" },
          { label: "공휴일(관리자)", code: "etc_holiday_manage" },
        ],
      },
      
      {
        title: "수입/지출 현황",
        items: [
          { label: "금천일자리(주) 수입 현황", code: "etc_income_status" },
          { label: "금천일자리(주) 지출 현황", code: "etc_expense_status" },
          { label: "금천일자리(주) 수입/지출 현황", code: "etc_total_status" },


        ],
      },
    ],
  };

  const [active, setActive] = useState("task");
  // ✅ 모바일에서는 하위 메뉴를 누른 버튼 바로 아래에 펼친다(아코디언).
  // active는 그대로 두고 "펼침 여부"만 따로 둔다 — active를 비우면 데스크톱으로
  // 넓혔을 때 아무 카드도 없는 화면이 된다.
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(true);

  const handleItemClick = (code, label, path) => {
    // ✅ 업무 관리 ========================================================================
    if (code === "task_assign_new")    { navigate("/task/assign/new");    return; }
    if (code === "task_assign_list")   { navigate("/task/assign/list");   return; }
    if (code === "task_received_list") { navigate("/task/received/list"); return; }
    if (code === "task_by_employee")  { navigate("/task/by-employee");              return; }
    if (code === "task_chat_mine")    { navigate("/task/chat", { state: { tab: "unread_mine"  }}); return; }
    if (code === "task_chat_other")   { navigate("/task/chat", { state: { tab: "unread_other" }}); return; }

    // ✅ 업무관리 ========================================================================
    if (code === "work_performance_quick")   { navigate("/work-performance/quick");   return; }
    if (code === "work_performance_summary") { navigate("/work-performance/summary"); return; }
    if (code === "work_performance_dashboard") { navigate("/work-performance/dashboard"); return; }

    
    // ✅ 교육 관리 ========================================================================
    if (code === "edu_job")     { navigate("/edu/job");     return; }
    if (code === "edu_youtube") { navigate("/edu/youtube"); return; }
    if (code === "edu_legal")   { navigate("/edu/legal");   return; }
    if (code === "edu_cert")    { navigate("/edu/cert");    return; }




    // ✅ 기안 작성 페이지 — 문서종류별 분리 페이지로 직접 진입(수입기안)
    if (code === "income_draft_new") {
      navigate("/approval/income-draft/new");
      return;
    }

    // ✅ 기안 작성 페이지 — 문서종류별 분리 페이지로 직접 진입(수입결의, 뼈대 → 독립 페이지)
    if (code === "income_resolution_new") {
      navigate("/approval/income-resolution/new");
      return;
    }

    // ✅ 기안 작성 페이지 — 문서종류별 분리 페이지로 직접 진입(지출기안)
    if (code === "draft_document_new") {
      navigate("/approval/expense-draft/new");
      return;
    }

    // ✅ 기안 작성 페이지 — 문서종류별 분리 페이지로 직접 진입(지출결의, 뼈대 스텁)
    if (code === "expense_resolution_new") {
      navigate("/approval/expense-resolution/new");
      return;
    }

    // ✅ 기안 목록 페이지
    if (code === "draft_document_list") {
      navigate("/approval/draftdocument/list");
      return;
    }

    // ✅ 결의 목록 페이지
    if (code === "resolution_list") {
      navigate("/approval/resolution-list");
      return;
    }

    // 수신결재 페이지 
    if (code === "approval_inbox") {
      navigate("/approval/inbox");
      return;
    }
    if (code === "approval_done") {
      navigate("/approval/done");
      return;
    }
    if (code === "approval_inprogress") {
      navigate("/approval/inprogress");
      return;
    }

    if (code === "approval_done2") {
      navigate("/approval/done");
      return;
    }
    if (code === "approval_cc") {
      navigate("/approval/cc");
      return;
    }
    if (code === "leave_request_new") {
      navigate("/approval/leave/new");
      return;
    }
    if (code === "leave_request_list") {
      navigate("/approval/leave/list");
      return;
    }

    // ✅ 수입데이터+수입기안 통합 작성 [3단계] — 환경/소독
    if (code === "income_env_disinfection_draft_new") {
      navigate("/income/env/disinfection/draft/new");
      return;
    }
    if (code === "income_env_disinfection_draft_list") {
      navigate("/income/env/disinfection/draft/list");
      return;
    }

    // (구) 환경/소독 수입 건
    if (code === "income_env_disinfection_new") {
      navigate("/income/env/disinfection/new");
      return;
    }

    // ✅ 수입데이터+수입기안 통합 작성 [파일럿] — 경영팀
    if (code === "income_business_draft_new") {
      navigate("/income/business/draft/new");
      return;
    }
    if (code === "income_business_draft_list") {
      navigate("/income/business/draft/list");
      return;
    }

    // (구) 경영팀 수입 건
    if (code === "income_business_new") {
      navigate("/income/business/new");
      return;
    }
    
    // 경영팀 수입목록
    if (code === "income_business_list") {
      navigate("/income/business/list");
      return;
    }
        
    // ✅ 수입데이터+수입기안 통합 작성 [4단계] — 용역사업
    if (code === "income_service_draft_new") {
      navigate("/income/service/draft/new");
      return;
    }
    if (code === "income_service_draft_list") {
      navigate("/income/service/draft/list");
      return;
    }

    // (구) 용역사업 수입 건
    if (code === "income_service_new") {
      navigate("/income/service/new");
      return;
    }

    // 용역사업 수입목록
    if (code === "income_service_list") {
      navigate("/income/service/list");
      return;
    }
    
    // CAFE_INN 가산점 수입 건
    if (code === "income_cafe_gasan_new") {
      navigate("/income/cafe/gasan/new");
      return;
    }

    // CAFE_INN 가산점 수입목록
    if (code === "income_cafe_gasan_list") {
      navigate("/income/cafe/gasan/list");
      return;
    }

    // CAFE_INN 독산점 수입 건
    if (code === "income_cafe_doksan_new") {
      navigate("/income/cafe/doksan/new");
      return;
    }   

    // CAFE_INN 독산점 수입목록
    if (code === "income_cafe_doksan_list") {
      navigate("/income/cafe/doksan/list");
      return;
    }    

    // CAFE_INN 금나래점 수입 건
    if (code === "income_cafe_geumnarae_new") {
      navigate("/income/cafe/geumnarae/new");
      return;
    }

    // CAFE_INN 금나래점 수입목록
    if (code === "income_cafe_geumnarae_list") {
      navigate("/income/cafe/geumnarae/list");
      return;
    }

    // CAFE_INN 그린점 수입 건
    if (code === "income_cafe_green_new") {
      navigate("/income/cafe/green/new");
      return;
    }

    // CAFE_INN 그린점 수입목록
    if (code === "income_cafe_green_list") {
      navigate("/income/cafe/green/list");
      return;
    }
    
    // ✅ 수입데이터+수입기안 통합 작성 [2단계] — 환경/종합
    if (code === "income_env_total_draft_new") {
      navigate("/income/env/total/draft/new");
      return;
    }
    if (code === "income_env_total_draft_list") {
      navigate("/income/env/total/draft/list");
      return;
    }

    // (구) 환경/종합 수입 건
    if (code === "income_env_total_new") {
      navigate("/income/env/total/new");
      return;
    }
    
    // 환경/종합 수입목록
    if (code === "income_env_total_list") {
      navigate("/income/env/total/list");
      return;
    }

    // 지출업무 ========================================================================
    // 경영팀 지출데이터+기안 작성(전 사업 확산 [3])
    if (code === "expense_business_draft_new") {
      navigate("/expense/business/draft/new");
      return;
    }

    // 경영팀 지출목록(신규)
    if (code === "expense_business_draft_list") {
      navigate("/expense/business/draft/list");
      return;
    }

    // ⚠️ (구) 단독 페이지 라우팅 — Dashboard 카드 버튼에서는 숨겼지만(SECTIONS.expense
    // 정리) URL 직접 접근은 계속 되게 이 분기와 실제 페이지 파일들은 남겨둔다. 통합
    // 작성 페이지(지출데이터+기안 작성)가 안정화됐다고 확정되면 이 (구) 코드 8쌍
    // 전부(expense_mgmt_*, expense_service_*, expense_env_disinfection_*,
    // expense_env_total_*, expense_cafe_*_new/list)와 대응 라우트·페이지 파일을
    // 일괄 삭제할 예정.
    // (구) 경영팀 지출 건
    if (code === "expense_mgmt_new") {
      navigate("/expense/business/new");
      return;
    }

    // (구) 경영팀 지출목록
    if (code === "expense_mgmt_list") {
      navigate("/expense/business/list");
      return;
    }

    // 테스트 지출 건
    if (code === "expense_test_new") {
      navigate("/expense/test/new");
      return;
    }

    // 테스트 지출목록
    if (code === "expense_test_list") {
      navigate("/expense/test/list");
      return;
    }

    // 용역사업 지출데이터+기안 작성(전 사업 확산 [4])
    if (code === "expense_service_draft_new") {
      navigate("/expense/service/draft/new");
      return;
    }

    // 용역사업 지출목록(신규)
    if (code === "expense_service_draft_list") {
      navigate("/expense/service/draft/list");
      return;
    }

    // (구) 용역사업 지출 건
    if (code === "expense_service_new") {
      navigate("/expense/service/new");
      return;
    }

    // (구) 용역사업 지출목록
    if (code === "expense_service_list") {
      navigate("/expense/service/list");
      return;
    }

    // 카페인 가산점 지출데이터+기안 작성(전 사업 확산 [5])
    if (code === "expense_cafe_gasan_draft_new") {
      navigate("/expense/cafe/gasan/draft/new");
      return;
    }

    // 카페인 가산점 지출목록(신규)
    if (code === "expense_cafe_gasan_draft_list") {
      navigate("/expense/cafe/gasan/draft/list");
      return;
    }

    // (구) CAFE_INN 가산점 지출 건
    if (code === "expense_cafe_gasan_new") {
      navigate("/expense/cafe/gasan/new");
      return;
    }

    // (구) CAFE_INN 가산점 지출목록
    if (code === "expense_cafe_gasan_list") {
      navigate("/expense/cafe/gasan/list");
      return;
    }

    // 카페인 독산점 지출데이터+기안 작성(전 사업 확산 [5])
    if (code === "expense_cafe_doksan_draft_new") {
      navigate("/expense/cafe/doksan/draft/new");
      return;
    }

    // 카페인 독산점 지출목록(신규)
    if (code === "expense_cafe_doksan_draft_list") {
      navigate("/expense/cafe/doksan/draft/list");
      return;
    }

    // (구) CAFE_INN 독산점 지출 건
    if (code === "expense_cafe_doksan_new") {
      navigate("/expense/cafe/doksan/new");
      return;
    }

    // (구) CAFE_INN 독산점 지출목록
    if (code === "expense_cafe_doksan_list") {
      navigate("/expense/cafe/doksan/list");
      return;
    }

    // 카페인 금나래점 지출데이터+기안 작성(전 사업 확산 [5])
    if (code === "expense_cafe_geumnarae_draft_new") {
      navigate("/expense/cafe/geumnarae/draft/new");
      return;
    }

    // 카페인 금나래점 지출목록(신규)
    if (code === "expense_cafe_geumnarae_draft_list") {
      navigate("/expense/cafe/geumnarae/draft/list");
      return;
    }

    // (구) CAFE_INN 금나래점 지출 건
    if (code === "expense_cafe_geumnarae_new") {
      navigate("/expense/cafe/geumnarae/new");
      return;
    }

    // (구) CAFE_INN 금나래점 지출목록
    if (code === "expense_cafe_geumnarae_list") {
      navigate("/expense/cafe/geumnarae/list");
      return;
    }

    // 카페인 그린점 지출데이터+기안 작성(전 사업 확산 [5])
    if (code === "expense_cafe_green_draft_new") {
      navigate("/expense/cafe/green/draft/new");
      return;
    }

    // 카페인 그린점 지출목록(신규)
    if (code === "expense_cafe_green_draft_list") {
      navigate("/expense/cafe/green/draft/list");
      return;
    }

    // (구) CAFE_INN 그린점 지출 건
    if (code === "expense_cafe_green_new") {
      navigate("/expense/cafe/green/new");
      return;
    }

    // (구) CAFE_INN 그린점 지출목록
    if (code === "expense_cafe_green_list") {
      navigate("/expense/cafe/green/list");
      return;
    }

    // 환경/소독 지출데이터+기안 작성(전 사업 확산 [2])
    if (code === "expense_env_disinfection_draft_new") {
      navigate("/expense/env/disinfection/draft/new");
      return;
    }

    // 환경/소독 지출목록(신규)
    if (code === "expense_env_disinfection_draft_list") {
      navigate("/expense/env/disinfection/draft/list");
      return;
    }

    // (구) 환경/소독 지출 건
    if (code === "expense_env_disinfection_new") {
      navigate("/expense/env/disinfection/new");
      return;
    }

    // (구) 환경/소독 지출목록
    if (code === "expense_env_disinfection_list") {
      navigate("/expense/env/disinfection/list");
      return;
    }

    // 환경/종합 지출데이터+기안 작성(통합 페이지 파일럿)
    if (code === "expense_env_total_draft_new") {
      navigate("/expense/env/total/draft/new");
      return;
    }

    // 환경/종합 지출목록(신규, 파일럿)
    if (code === "expense_env_total_draft_list") {
      navigate("/expense/env/total/draft/list");
      return;
    }

    // (구) 환경/종합 지출 건
    if (code === "expense_env_total_new") {
      navigate("/expense/env/total/new");
      return;
    }

    // (구) 환경/종합 지출목록
    if (code === "expense_env_total_list") {
      navigate("/expense/env/total/list");
      return;
    }
        
    // 회계업무 -------------------------------------------------------------------------------------
    if (code === "acct_general_income_form") {      // (일반) 매출자료 작성
      navigate("/accounting/general/sales/new");
      return;
    }

    if (code === "acct_general_income_list") {      // (일반) 매출자료 목록
      navigate("/accounting/general/sales/list");
      return;
    }

    if (code === "acct_account_sales_form") {       // (일반) 매출 계정과목 작성
      navigate("/accounting/accounts/sales/new");
      return;
    }
    if (code === "acct_account_sales_list") {       // (일반) 매출 계정과목 목록
      navigate("/accounting/accounts/sales-list");
      return;
    }    
    if (code === "acct_bank_register") {
      navigate("/accounting/bank/register", {
        replace: true,
        state: { tabTitle: "통장 등록" },
      });
      return;
    }

    // 예산서
    if (code === "acct_budget_yearly_form") { navigate("/accounting/budget/yearly/new");  return; }
    if (code === "acct_budget_yearly_list") { navigate("/accounting/budget/yearly/list"); return; }


    // 사회보험료
    if (code === "etc_insurance_allocation") {     

      navigate("/etc/insurance-allocation");
      return;
    }

    // ✅ 개인별 연간 사회보험료 합계
    if (code === "etc_insurance_annual") {
      navigate("/etc/insurance-annual");
      return;
    }

    // ✅ 4대보험 요율 관리
    if (code === "insurance_rate_manage") {
      navigate("/payroll/insurance-rates");
      return;
    }

    // ✅ 급여 — 근로소득 간이세액표
    if (code === "payroll_tax_table") {
      navigate("/payroll/tax-table");
      return;
    }
    // ✅ 급여 — 시급 설정
    if (code === "payroll_wage_rates") {
      navigate("/payroll/wage-rates");
      return;
    }
    // ✅ 급여명세서

    if (code === "payroll_sheet") { navigate("/payroll/sheet"); return; }

    


    // 기성계 자동화
    if (code === "gisung_contract_list") { navigate("/gisung-gye/contracts");  return; }
    if (code === "gisung_guchung")       { navigate("/gisung-gye/guchung");     return; }
    if (code === "gisung_gongdan")       { navigate("/gisung-gye/gongdan");     return; }
    if (code === "gisung_history")       { navigate("/gisung-gye/history");     return; }

    // 인사평가 
    if (code === "hreval_list")     { navigate("/hreval/list");     return; }   // 일용직
    if (code === "hreval_contract") { navigate("/hreval/contract"); return; }   // 계약직

    // 경영평가
    if (code === "evaluation_manual_list") { navigate("/evaluation/manuals"); return; }


    // 기타업무 ========================================================================
    if (code === "etc_staff_contacts") {               // 임직원
      navigate("/etc/staff-contacts");
      return;
    }
    if (code === "etc_vendors") {                       // 거래처
      navigate("/etc/vendors");
      return;
    }

    if (code === "etc_websites") { navigate("/etc/websites"); return; } // 웹사이트 

    // ✅ 팀장 사용설명서
    if (code === "etc_manual_guide") { navigate("/manual-guide"); return; }

    if (code === "etc_expense_usage_codes") {
      navigate("/etc/expense-usage-codes");
      return;
    }

    if (code === "etc_expense_usage_codes") {           // 지출 사용내역 관리
      navigate("/etc/expense-usage-codes");
      return;
    }

    if (code === "cafe_work_schedule") {                 // 업무일정표(카페 업무 그룹)
      navigate("/work-schedule");
      return;
    }

    if (code === "cafe_expense_ledger") {               // 카페 지출 기안 대장
      navigate("/cafe/expense-ledger");
      return;
    }

    if (code === "etc_mail_alert") {                     // 공용메일 알림
      navigate("/etc/mail-alert");
      return;
    }

    if (code === "rental_orders") {                      // 금천아 빌려줘 (대여 주문)
      navigate("/rental/orders");
      return;
    }

    if (code === "aircon_reservations") {                // 에어컨 청소 예약(홈페이지 연동)
      navigate("/aircon/reservations");
      return;
    }

    if (code === "rental_sales") {                       // 금천아 빌려줘 매출 현황
      navigate("/rental/sales");
      return;
    }

    if (code === "etc_calendar_general") {
      navigate("/etc/calendar/general");
      return;
    }
    if (code === "etc_calendar_admin") {
      navigate("/etc/calendar/admin");
      return;
    }

    if (code === "etc_holiday_manage") {   // 공휴일(관리자)
      navigate("/etc/holiday/manage");
      return;
    }



    // 수입/지출 현황 관련
    if (code === "etc_income_status") {
      navigate("/etc/income-status");
      return;
    }
    if (code === "etc_expense_status") {
      navigate("/etc/expense-status");
      return;
    }
    if (code === "etc_total_status") {
      navigate("/etc/total-status");
      return;
    }

    // 복지포인트
    if (code === "etc_welfare_point") {
      navigate("/welfare/point");
      return;
    }


    // 일별 TO DO (아침 회의)
    if (code === "daily_todo_list")    { navigate("/daily-todo");         return; }
    if (code === "daily_todo_meeting") { navigate("/daily-todo/meeting"); return; }
    if (code === "daily_todo_stats")   { navigate("/daily-todo/stats");   return; }


    
    if (path) {
      navigate(path);
      return;
    }
    alert(`[${label}] 화면은 준비 중입니다.\n(code: ${code})`);
  };

  // 카드 + 메뉴 렌더 — 모바일(버튼 바로 아래)과 데스크톱(탭 밑)이 같은 코드를 쓴다.
  // 마크업을 두 군데 복사하면 한쪽만 고쳐지는 일이 생긴다.
  const renderSections = (tabKey) => (
    <div className={styles.grid}>
      {(SECTIONS[tabKey] || []).map((sec) => (
        <React.Fragment key={sec.title}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{sec.title}</div>
            <ul className={styles.menu}>
              {sec.items.map((it) => (
                <li key={it.code} className={styles.menuItem}>
                  <button
                    type="button"
                    className={styles.menuLink}
                    onClick={() => handleItemClick(it.code, it.label, it.path)}
                  >
                    {it.label}
                    {it.badge > 0 && (
                      <span style={{
                        marginLeft: 6,
                        background: "#e74c3c", color: "#fff",
                        borderRadius: "50%", fontSize: "11px",
                        padding: "1px 6px", fontWeight: 700,
                      }}>
                        {it.badge}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {sec.breakAfter && <div aria-hidden="true" />}
        </React.Fragment>
      ))}
    </div>
  );

  // 탭 클릭 — 모바일에서 같은 카테고리를 다시 누르면 접는다.
  const handleTabClick = (t) => {
    if (t.navigateTo) {
      navigate(t.navigateTo);
      return;
    }
    if (isMobile && active === t.key) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setActive(t.key);
    setMobileOpen(true);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>
        {/* 탭 버튼 */}
        <div className={styles.tabs}>
          {TABS.map((t) => {
            const expanded = isMobile && active === t.key && mobileOpen;
            return (
              <React.Fragment key={t.key}>
                <button
                  className={active === t.key ? styles.tabActive : styles.tab}
                  onClick={() => handleTabClick(t)}
                  type="button"
                  aria-expanded={isMobile ? expanded : undefined}
                >
                  {t.label}
                </button>

                {/* 모바일 아코디언 — 누른 버튼 바로 아래에 전체 너비로 펼친다.
                    .tabs가 그리드라서 gridColumn 1/-1로 한 줄을 통째로 차지한다. */}
                {expanded && (
                  <div style={{ gridColumn: "1 / -1" }}>{renderSections(t.key)}</div>
                )}

                {t.breakAfter && (
                  <div aria-hidden="true" style={{ gridColumn: "1 / -1", height: 0 }} />
                )}


              </React.Fragment>
            );
          })}
        </div>

        {/* 카드 + 메뉴 — 데스크톱은 기존처럼 탭 아래에 둔다
            (모바일은 위 아코디언에서 이미 렌더했으므로 중복 렌더하지 않는다) */}
        {!isMobile && renderSections(active)}


      </div>
    </div>
  );
}


